import React, { memo } from "react";
import { ViewStyle } from "react-native";
import {
  InputToolbar,
  InputToolbarProps,
  IMessage,
} from "react-native-gifted-chat";

import ResearchToggleButton from "@/features/knowledge-graph/components/molecules/ResearchToggleButton";
import { bubbleMaxWidth } from "@/features/knowledge-graph/utils/bubbleMaxWidth";

import { Box } from "@/components/atoms/Box";

import { TEST_ID_GEMINI_CHAT } from "e2e/testId";

/*
 * design-system: dynamic-style waiver — SE-7153. The composer has to track the
 * same continuous fraction of the window as the message bubbles it sits under,
 * and no closed sizing token expresses a fraction of the viewport. Reusing
 * `bubbleMaxWidth` rather than restating the ratio is the point: SE-7084
 * created that ladder precisely so the two chat renderers could not drift
 * apart, and an independent cap here is how they drifted. The largest step on
 * the closed scale (`max-w-96`, 384px) is what this replaces — it was a third
 * of the panel's width at 1920px. Passed as a single style reference so no
 * inline object literal renders on a JSX element.
 */
const composerMaxWidthStyle = (windowWidth: number): ViewStyle => ({
  maxWidth: bubbleMaxWidth(windowWidth),
});

/**
 * Props for ChatInputToolbarView
 */
interface ChatInputToolbarViewProps {
  props: InputToolbarProps<IMessage>;
  windowWidth: number;
  isDark: boolean;
  isResearchToggleEnabled: boolean;
  isResearchMode: boolean;
  onToggleResearch: () => void;
}

/**
 * View component for chat input toolbar with responsive layout
 * @param root0 - Component props
 * @param root0.props - Gifted Chat input toolbar props containing text input and send handlers
 * @param root0.windowWidth - Current window width for responsive layout switching
 * @param root0.isDark - Whether dark theme is active
 * @param root0.isResearchToggleEnabled - Whether research mode toggle button should be displayed
 * @param root0.isResearchMode - Whether deep research mode is currently enabled
 * @param root0.onToggleResearch - Callback invoked when research mode toggle is pressed
 */
const ChatInputToolbarView = ({
  props,
  windowWidth,
  isDark,
  isResearchToggleEnabled,
  isResearchMode,
  onToggleResearch,
}: ChatInputToolbarViewProps) =>
  windowWidth < 1280 ? (
    <>
      {isResearchToggleEnabled && (
        <Box className="px-3 py-2">
          <ResearchToggleButton
            isResearchMode={isResearchMode}
            isDark={isDark}
            onToggle={onToggleResearch}
          />
        </Box>
      )}
      <InputToolbar
        {...props}
        containerStyle={{
          borderTopWidth: 1,
          borderTopColor: isDark ? "#2c2c2c" : "#e5e5e5",
          paddingTop: 0,
          paddingBottom: 0,
          paddingHorizontal: 0,
          backgroundColor: isDark ? "#1a1a1a" : "#fff",
          minHeight: 40,
          marginHorizontal: 0,
          marginBottom: 0,
          marginTop: 0,
        }}
      />
    </>
  ) : (
    <Box className="items-center px-4 pb-2 pt-4">
      <Box
        className="w-full"
        testID={TEST_ID_GEMINI_CHAT.COMPOSER}
        // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: measured responsive maxWidth from windowWidth (continuous multiplier); no closed sizing token
        UNSAFE_style={
          composerMaxWidthStyle(windowWidth) as Record<string, string | number>
        }
      >
        <Box className="relative">
          <InputToolbar
            {...props}
            containerStyle={{
              borderTopWidth: 1,
              borderTopColor: isDark ? "#444" : "#e0e0e0",
              paddingTop: 16,
              paddingBottom: 16,
              paddingHorizontal: 16,
              backgroundColor: isDark ? "#1a1a1a" : "#fff",
              borderRadius: 16,
              borderWidth: 1,
              borderColor: isDark ? "#333" : "#e0e0e0",
              minHeight: 80,
            }}
          />
          {isResearchToggleEnabled && (
            <Box className="absolute bottom-4 right-16">
              <ResearchToggleButton
                isResearchMode={isResearchMode}
                isDark={isDark}
                onToggle={onToggleResearch}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

ChatInputToolbarView.displayName = "ChatInputToolbarView";

export default memo(ChatInputToolbarView);
